/**
 * Description: Unified product editor page for both creating and editing products
 */

import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import ProductForm from '@components/ProductForm'
import { api, Product } from '@services/api'
import { toast } from 'react-toastify'

const ProductEditor: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEditMode = !!id

  const [product, setProduct] = useState<Product | null>(null)
  const [isLoading, setIsLoading] = useState(isEditMode)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (isEditMode) {
      loadProduct()
    }
  }, [id])

  const loadProduct = async () => {
    if (!id) return

    try {
      setIsLoading(true)
      const data = await api.getProductById(parseInt(id))
      setProduct(data)
    } catch (error) {
      toast.error('Failed to load product')
      // eslint-disable-next-line no-console
      console.error(error)
      navigate('/products')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (data: Product) => {
    try {
      setIsSaving(true)

      if (isEditMode && id) {
        // Update existing product
        await api.updateProduct(parseInt(id), data)
        toast.success(`Product "${data.product_name}" updated successfully!`)
        navigate(`/products/${id}`)
      } else {
        // Create new product
        const result = await api.createProduct(data)
        toast.success(`Product "${data.product_name}" created successfully!`)
        navigate(`/products/${result.id}`)
      }
    } catch (error: any) {
      // Extract validation error messages if available
      let errorMessage = `Failed to ${isEditMode ? 'update' : 'create'} product`

      if (error.response?.data?.details && Array.isArray(error.response.data.details)) {
        // Format validation errors
        const validationErrors = error.response.data.details.map((err: any) => `${err.message}`).join('; ')
        errorMessage = validationErrors
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error
      }

      toast.error(errorMessage)
      // eslint-disable-next-line no-console
      console.error(error)
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="card py-12 text-center">
        <div className="border-primary-600 inline-block h-12 w-12 animate-spin rounded-full border-b-2"></div>
        <p className="mt-4 text-gray-500 dark:text-gray-400">Loading product...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate(isEditMode && id ? `/products/${id}` : '/products')}
          className="cursor-pointer rounded-lg p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {isEditMode ? 'Edit Product' : 'Create New Product'}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {isEditMode ? product?.product_name : 'Add a new product to the catalog'}
          </p>
        </div>
      </div>

      <div className="card">
        <ProductForm
          product={product || undefined}
          onSubmit={handleSubmit}
          onCancel={() => navigate(isEditMode && id ? `/products/${id}` : '/products')}
          isLoading={isSaving}
        />
      </div>
    </div>
  )
}

export default ProductEditor
